import { inject, Injectable } from '@angular/core';
import { AccountService } from '../../account.service';
import { catchError, EMPTY, filter, map, mergeMap, of, switchMap, tap, throwError, withLatestFrom } from 'rxjs';
import { newPasswordApiActions } from './actions/new-password-api.actions';
import { newPasswordFeaturesActions } from './actions/new-password-features.actions';
import { selectNewPasswordToken, selectNewPasswordUserId, selectUserEmail } from './new-password.selectors';
import { SSOHttpErrorResponse } from '../../models/errors.model';
import { loginFeatureActions } from '../../login/state/actions/login-feature.actions';
import { uiActions } from '../../state/ui.actions';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { RecaptchaService } from '../../core/recaptcha/recaptcha.service';

@Injectable()
export class NewPasswordEffects {
  private store = inject(Store);
  private router = inject(Router);
  private actions$ = inject(Actions);
  private accountService = inject(AccountService);
  private recaptchaService = inject(RecaptchaService);

  verifyResetPasswordToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newPasswordFeaturesActions.verifyResetPasswordToken),
      mergeMap(({ token, userId }) =>
        this.recaptchaService.execute('submit').pipe(
          switchMap((recaptchaToken) => {
            return this.accountService.verifyResetPasswordToken(+userId, token, recaptchaToken).pipe(
              map((response) =>
                response.status === HttpStatusCode.NoContent
                  ? newPasswordApiActions.resetPasswordTokenVerifiedSuccessfully()
                  : newPasswordApiActions.resetPasswordTokenVerificationFailed({
                      userEmail: response.body ? response.body.email : '',
                    }),
              ),
              catchError((httpErrorResponse: SSOHttpErrorResponse) => {
                this.store.dispatch(newPasswordApiActions.resetPasswordTokenVerificationFailed({ userEmail: null }));
                return httpErrorResponse.error ? EMPTY : throwError(() => httpErrorResponse);
              }),
            );
          }),
          catchError((error) => (error instanceof HttpErrorResponse ? throwError(() => error) : of(loginFeatureActions.reCaptchaFailed()))),
        ),
      ),
    ),
  );

  setNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newPasswordFeaturesActions.setNewPassword),
      withLatestFrom(this.store.select(selectNewPasswordUserId), this.store.select(selectNewPasswordToken)),
      mergeMap(([{ password, passwordConfirmed }, userId, token]) => {
        return this.recaptchaService.execute('submit').pipe(
          switchMap((recaptchaToken) => {
            return this.accountService.setNewPassword({ password, passwordConfirmed, token, userId, recaptchaToken }).pipe(
              map(() => newPasswordApiActions.passwordChangeSuccessfully()),
              catchError((httpErrorResponse: SSOHttpErrorResponse) => {
                this.store.dispatch(newPasswordApiActions.passwordChangeFailed({ error: httpErrorResponse.error || null }));
                return httpErrorResponse.error ? EMPTY : throwError(() => httpErrorResponse);
              }),
            );
          }),
          catchError((error) => (error instanceof HttpErrorResponse ? throwError(() => error) : of(loginFeatureActions.reCaptchaFailed()))),
        );
      }),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newPasswordFeaturesActions.resetPassword),
      withLatestFrom(this.store.select(selectUserEmail).pipe(filter(Boolean))),
      switchMap(([, userName]) => {
        return this.recaptchaService.execute('submit').pipe(
          switchMap((recaptchaToken) => {
            return this.accountService.resetPassword({ userName, recaptchaToken }).pipe(
              tap(() =>
                this.store.dispatch(uiActions.displaySuccessToast({ toastMessage: 'NEW_PASSWORD.MESSAGES.RESET_PASSWORD_RESENT' })),
              ),
              map(() => newPasswordApiActions.resetPasswordSuccessfully()),
              catchError((httpErrorResponse: SSOHttpErrorResponse) => {
                this.store.dispatch(newPasswordApiActions.resetPasswordFailed({ error: httpErrorResponse.error || null }));
                return httpErrorResponse.error ? EMPTY : throwError(() => httpErrorResponse);
              }),
            );
          }),
          catchError((error) =>
            error instanceof HttpErrorResponse ? throwError(() => error) : of(newPasswordFeaturesActions.reCaptchaFailed()),
          ),
        );
      }),
    ),
  );

  loadPasswordPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newPasswordApiActions.resetPasswordTokenVerifiedSuccessfully),
      mergeMap(() =>
        this.accountService
          .fetchPasswordPolicyStrength()
          .pipe(map(({ policyStrength }) => newPasswordApiActions.passwordPolicyLoadedSuccessfully({ policy: policyStrength }))),
      ),
    ),
  );

  passwordChangedSuccessfully = createEffect(() =>
    this.actions$.pipe(
      ofType(newPasswordApiActions.passwordChangeSuccessfully),
      tap(() => this.router.navigate(['/login'])),
      map(() => uiActions.displaySuccessToast({ toastMessage: 'NEW_PASSWORD.MESSAGES.PASSWORD_CHANGED' })),
    ),
  );
}
